import { useNavigation } from '@react-navigation/native';
import { styled, SxProp, useSx } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import * as Linking from 'expo-linking';

import { IS_WEB, parseApiError, passIf } from '@utils';
import { AnalyticsService, ToastService } from '@services';
import {
  ProfileNavigatorNavigationPropType,
  screens,
  ScreensNameType,
} from '@navigation';
import {
  BaseText,
  BreadcrumbBar,
  Button,
  ButtonsLayout,
  ButtonStyleEnum,
  Field,
  Form,
  FormButton,
  FormPickerField,
  MultilineField,
  ScreenLoader,
  Separator,
  WebTitle,
  BaseTextLink,
} from '@components';
import { CONTACT_EMAIL, synthoURLs } from '@constants';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { useSupportModel } from '@models/Support/SupportModel';
import {
  contactUsSchema,
  ContactUsSchemaDataType,
} from '@validation';

const ScrollViewContainer = styled(KeyboardAwareScrollView)({
  bg: '$background0',
  flex: 1,
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return {
    flexGrow: 1,
    pt: '$4',
    px: ['$6', '5%'],
    pb: props.bottom,
  };
}

const SubTitle = styled(BaseText)({
  maxWidth: 600,
  pt: ['$5', '$2'],
  variant: 'text.medium',
  color: '$text400',
});

const SubTitleSemi = styled(BaseText)({
  variant: 'text.medium',
  color: '$text400',
  fontWeight: 'semi',
});

const FormInnerContainer = styled(View)({
  maxWidth: 600,
  flex: 1,
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const FieldsContainer = styled(View)({
  flexGrow: [1, 0],
  // We are using zIndex to make a dropdown for enquiryTypeId
  // appear over the submit/cancel buttons otherwise this dropdown
  // menu will appear under the the submit/cancel buttons
  zIndex: passIf(IS_WEB, 1),
});

const ContactEmailFieldContainer = styled(View)({
  flex: 1,
});

const Label = styled(BaseText)({
  variant: 'text.small',
  color: '$text400',
  pr: '$4',
});

const cancelButtonStyle = IS_WEB
  ? ButtonStyleEnum.Outline
  : ButtonStyleEnum.Ghost;

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Help,
  screens.ContactUs,
];

type ContainerRefType = View | null;

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'ContactUs'>;

export const ContactUsScreen = observer(() => {
  const sx = useSx();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();
  const containerRef = useRef<ContainerRefType>(null);
  const support = useSupportModel();

  useNavOptionsSet({ title: t('contactUs.title') });

  useAsyncInit(async () => {
    try {
      await support.contactUs.fetchEnquiryTypes.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  async function sendContactUsMessage(
    values: ContactUsSchemaDataType,
  ) {
    try {
      await support.contactUs.sendMessage.run(values);

      ToastService.showSuccess(t('contactUs.successMessage'));

      AnalyticsService.trackUserContactUs(
        values.enquiryTypeId,
        values.description,
      );

      closeContactUs();
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  function openUrl() {
    if (IS_WEB) {
      window.open(synthoURLs.FAQ, '_blank');
    } else {
      Linking.openURL(synthoURLs.FAQ);
    }
  }

  function closeContactUs() {
    // We use navigate method here instead of goBack
    // to properly handle cancel button on web if page has been refreshed
    if (nav.canGoBack()) {
      nav.goBack();
    } else {
      nav.navigate(screens.Help);
    }
  }

  const initialValues: ContactUsSchemaDataType = {
    // Initial value of enquiryTypeId must be an empty string
    // for proper validation
    enquiryTypeId: '' as unknown as number,
    description: '',
  };

  if (support.contactUs.fetchEnquiryTypes.inProgress) {
    return <ScreenLoader />;
  }

  return (
    <ScrollViewContainer
      contentContainerStyle={sx(contentContainerStyle(insets))}
    >
      {IS_WEB && (
        <Fragment>
          <BreadcrumbBar routes={BREADCRUMB_ROUTES} />
          <WebTitle>{t('contactUs.title')}</WebTitle>
        </Fragment>
      )}

      <SubTitle>
        <Trans
          i18nKey={t('contactUs.subtitle')}
          components={{
            bold: <SubTitleSemi />,
            url: <BaseTextLink onPress={openUrl} />,
          }}
        />
      </SubTitle>
      <Separator />

      <Form<ContactUsSchemaDataType>
        initialValues={initialValues}
        validationSchema={contactUsSchema}
        onSubmit={sendContactUsMessage}
      >
        <FormInnerContainer ref={containerRef}>
          <FieldsContainer>
            <RowContainer>
              <Label>{t('contactUs.to')}</Label>
              <ContactEmailFieldContainer>
                <Field
                  name="contactEmail"
                  disabled
                  value={CONTACT_EMAIL}
                />
              </ContactEmailFieldContainer>
            </RowContainer>

            <Separator />

            <FormPickerField
              name="enquiryTypeId"
              containerRef={containerRef}
              placeholder={t('contactUs.enquiryType')}
              options={support.contactUs.preparedEnquiryTypes}
            />

            <Separator />
            <MultilineField
              name="description"
              maxLength={300}
              placeholder={t('contactUs.message')}
            />
          </FieldsContainer>

          <Separator />

          <ButtonsLayout>
            <FormButton>{t('contactUs.send')}</FormButton>
            <Button
              buttonStyle={cancelButtonStyle}
              onPress={closeContactUs}
            >
              {t('contactUs.cancel')}
            </Button>
          </ButtonsLayout>
          <Separator size="$10" />
        </FormInnerContainer>
      </Form>
    </ScrollViewContainer>
  );
});
